/**
 * XPEN-3562: Split one amount into parts by percentage, to the exact cent.
 *
 * Used when one invoice line is divided between several people: the parts must
 * always add back to the original amount, with no cent lost or invented.
 *
 * Method — whole cents, on the magnitude:
 * - the sign is set aside and the work is done on the absolute amount, so a
 *   negative total (a credit note) is never rounded away from zero;
 * - each part keeps its whole cents, and the cents left over are handed out one
 *   at a time to the parts that lost the largest fraction, ties going to the
 *   lowest index so the same input always gives the same result.
 *
 * Negative percentages count as 0 and percentages summing to 0 split evenly, as
 * the backend's `_allocate_proportionally` does. The total is rounded to the cent
 * first, so it is meant for amounts of at most two decimals.
 *
 * @param {number} total - The amount to split.
 * @param {number[]} percentages - One weight per part; they need not sum to 100.
 * @returns {number[]} One amount per part, summing exactly to `total` in cents.
 */
export function allocateByPercentage(total, percentages) {
  const count = Array.isArray(percentages) ? percentages.length : 0;
  if (count === 0) {
    return [];
  }
  if (!Number.isFinite(total)) {
    return Array(count).fill(0);
  }

  const sign = total < 0 ? -1 : 1;
  const cents = Math.round(Math.abs(total) * 100);

  const weights = percentages.map((percentage) => {
    const weight = Number(percentage);
    return Number.isFinite(weight) && weight > 0 ? weight : 0;
  });
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);

  // Multiply before dividing so a 29/71 split works on whole numbers, not on 0.29.
  const exactShares = weightSum > 0
    ? weights.map((weight) => (cents * weight) / weightSum)
    : weights.map(() => cents / count);
  const wholeShares = exactShares.map((share) => Math.floor(share));
  const leftover = cents - wholeShares.reduce((sum, share) => sum + share, 0);

  const receivesExtraCent = new Set(
    exactShares
      .map((share, index) => ({ index, fraction: share - wholeShares[index] }))
      .sort((a, b) => b.fraction - a.fraction || a.index - b.index)
      .slice(0, leftover)
      .map(({ index }) => index)
  );

  return wholeShares.map((share, index) => {
    const partCents = share + (receivesExtraCent.has(index) ? 1 : 0);
    const amount = (sign * partCents) / 100;
    return amount === 0 ? 0 : amount; // never return -0
  });
}
